import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { employeeService } from '../services/api';
import { declarationService } from '../services/declarationService';
import { payrollService } from '../services/payrollService';
import { companyService } from '../services/companyService';
import './EmployeeManagement.css';
import './PayrollManagement.css';

const DeclarationManagement = () => {
  const { hasModuleAccess } = useAuth();
  const hasDeclarationAccess = hasModuleAccess('DECLARATIONS');
  const hasPayrollAccess = hasModuleAccess('PAYROLLS');

  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [mode, setMode] = useState('month'); // month | year | range
  const [startYear, setStartYear] = useState(new Date().getFullYear());
  const [startMonth, setStartMonth] = useState(new Date().getMonth() + 1);
  const [endYear, setEndYear] = useState(new Date().getFullYear());
  const [endMonth, setEndMonth] = useState(new Date().getMonth() + 1);
  const [declarations, setDeclarations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [sortBy, setSortBy] = useState('period');
  const [sortDir, setSortDir] = useState('desc');
  
  // États pour la génération du fichier de déclaration CNSS
  const [declarationYear, setDeclarationYear] = useState(new Date().getFullYear());
  const [declarationQuarter, setDeclarationQuarter] = useState(null);
  const [companyCnssId, setCompanyCnssId] = useState('');
  const [generatingDeclarationFile, setGeneratingDeclarationFile] = useState(false);

  const buildCnssDeclarationFileName = (cnssId, quarter, year) => {
    const clean = String(cnssId || '0').replace(/[-\s]/g, '');
    const employer = clean.padStart(10, '0').slice(-10);
    return `DS${employer}0000.${quarter}${year}`;
  };

  const extractFileNameFromHeaders = (headers) => {
    const disposition = headers?.['content-disposition'] || headers?.['Content-Disposition'];
    if (!disposition) return null;
    const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (!match?.[1]) return null;
    return match[1].replace(/['"]/g, '');
  };

  const loadEmployees = useCallback(async () => {
    try {
      const res = await employeeService.getAll();
      setEmployees(res.data || []);
    } catch (e) {
      // ignore
    }
  }, []);

  const loadCompanyInfo = useCallback(async () => {
    try {
      const res = await companyService.getInfo();
      setCompanyCnssId(res.data?.cnssId || '');
    } catch (e) {
      // ignore
    }
  }, []);

  const loadDeclarations = useCallback(async () => {
    if (!selectedEmployeeId) {
      setDeclarations([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      let res;
      if (mode === 'year') {
        res = await declarationService.getByEmployeeYear(selectedEmployeeId, year);
      } else if (mode === 'range') {
        if (!startYear || !startMonth || !endYear || !endMonth) {
          setError('Sélectionnez une période de début et de fin');
          setLoading(false);
          return;
        }
        const startVal = startYear * 12 + startMonth;
        const endVal = endYear * 12 + endMonth;
        if (startVal > endVal) {
          setError('La date de début doit être antérieure à la date de fin');
          setLoading(false);
          return;
        }
        res = await declarationService.getByEmployeeRange(selectedEmployeeId, startYear, startMonth, endYear, endMonth);
      } else {
        res = await declarationService.getByEmployee(selectedEmployeeId, year, month);
      }
      setDeclarations(res.data || []);
      setPage(1);
    } catch (e) {
      setError('Erreur lors du chargement des déclarations');
    } finally {
      setLoading(false);
    }
  }, [selectedEmployeeId, year, month, mode, startYear, startMonth, endYear, endMonth]);

  useEffect(() => {
    loadEmployees();
    loadCompanyInfo();
  }, [loadEmployees, loadCompanyInfo]);

  useEffect(() => {
    loadDeclarations();
  }, [loadDeclarations]);

  const filteredEmployees = employees.filter((emp) => {
    if (!employeeSearch) return true;
    const q = employeeSearch.toLowerCase();
    const fullName = `${emp.nom || ''} ${emp.prenom || ''}`.toLowerCase();
    const matricule = (emp.matricule || '').toLowerCase();
    const cin = (emp.cin || '').toLowerCase();
    const cnss = (emp.cnssNumber || '').toLowerCase();
    return fullName.includes(q) || matricule.includes(q) || cin.includes(q) || cnss.includes(q);
  });

  const handleSelectEmployee = (emp) => {
    if (emp) {
      setSelectedEmployeeId(emp.id);
      setEmployeeSearch(`${emp.nom} ${emp.prenom}`);
    } else {
      setSelectedEmployeeId(null);
      setEmployeeSearch('');
    }
    setShowDropdown(false);
  };

  const handleCalculate = async () => {
    if (!selectedEmployeeId) {
      setError('Sélectionnez un employé');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await declarationService.calculate(selectedEmployeeId, year, month);
      await loadDeclarations();
    } catch (e) {
      setError("Erreur lors du calcul de la déclaration");
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async (id) => {
    setLoading(true);
    setError('');
    try {
      await declarationService.validate(id);
      await loadDeclarations();
    } catch (e) {
      setError("Erreur lors de la validation");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (id) => {
    setLoading(true);
    setError('');
    try {
      await declarationService.submit(id);
      await loadDeclarations();
    } catch (e) {
      setError("Erreur lors de la soumission");
    } finally {
      setLoading(false);
    }
  };

  const handleExportPdf = async () => {
    if (!selectedEmployeeId) {
      setError('Sélectionnez un employé');
      return;
    }
    try {
      if (mode === 'year') {
        await declarationService.exportPdfYear(selectedEmployeeId, year);
      } else if (mode === 'range') {
        await declarationService.exportPdfRange(selectedEmployeeId, startYear, startMonth, endYear, endMonth);
      } else {
        await declarationService.exportPdf(selectedEmployeeId, year, month);
      }
    } catch (e) {
      setError("Erreur lors de l'export PDF");
    }
  };

  const handleExportExcel = async () => {
    if (!selectedEmployeeId) {
      setError('Sélectionnez un employé');
      return;
    }
    try {
      if (mode === 'year') {
        await declarationService.exportExcelYear(selectedEmployeeId, year);
      } else if (mode === 'range') {
        await declarationService.exportExcelRange(selectedEmployeeId, startYear, startMonth, endYear, endMonth);
      } else {
        await declarationService.exportExcel(selectedEmployeeId, year, month);
      }
    } catch (e) {
      setError("Erreur lors de l'export Excel");
    }
  };

  const handleExportPdfConsolidated = async () => {
    try {
      await declarationService.exportPdfConsolidated(year, month);
    } catch (e) {
      setError("Erreur lors de l'export PDF consolidé");
    }
  };

  const handleExportExcelConsolidated = async () => {
    try {
      await declarationService.exportExcelConsolidated(year, month);
    } catch (e) {
      setError("Erreur lors de l'export Excel consolidé");
    }
  };

  const monthsOptions = Array.from({ length: 12 }, (_, i) => i + 1);

  const sorted = [...declarations].sort((a, b) => {
    if (sortBy === 'period') {
      const valA = (a.periodYear || 0) * 12 + (a.periodMonth || 0);
      const valB = (b.periodYear || 0) * 12 + (b.periodMonth || 0);
      return sortDir === 'asc' ? valA - valB : valB - valA;
    }
    if (sortBy === 'net') {
      const valA = a.netSalary || 0;
      const valB = b.netSalary || 0;
      return sortDir === 'asc' ? valA - valB : valB - valA;
    }
    return 0;
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const paginated = sorted.slice((page - 1) * pageSize, page * pageSize);

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };

  const handleGenerateDeclarationFile = async () => {
    if (!declarationQuarter) {
      setError('Veuillez sélectionner un trimestre');
      return;
    }
    
    setGeneratingDeclarationFile(true);
    setError('');
    try {
      const response = await payrollService.generateQuarterlyDeclarationFile(declarationYear, declarationQuarter);
      
      let fileName = buildCnssDeclarationFileName(
        companyCnssId,
        declarationQuarter,
        declarationYear
      );
      const headerFileName = extractFileNameFromHeaders(response.headers);
      if (headerFileName) {
        fileName = headerFileName;
      }
      
      // Créer un blob et télécharger
      const blob = new Blob([response.data], { type: 'text/plain;charset=iso-8859-1' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Erreur lors de la génération du fichier de déclaration: ' + (err.response?.data?.error || err.message));
      console.error('Error generating declaration file:', err);
    } finally {
      setGeneratingDeclarationFile(false);
    }
  };

  return (
    <div className="employee-management">
      <div className="management-header">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <h2>Déclarations fiscales et sociales</h2>
          <span style={{ fontSize: 13, color: '#666' }}>Calcul, validation et export PDF/XLSX</span>
        </div>
        <div className="actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {hasDeclarationAccess && (
            <>
              <button className="btn btn-primary" onClick={handleCalculate}>Calculer</button>
              <button className="btn" onClick={handleExportPdf}>Export PDF</button>
              <button className="btn" onClick={handleExportExcel}>Export XLSX</button>
              <button className="btn" onClick={handleExportPdfConsolidated}>PDF consolidé</button>
              <button className="btn" onClick={handleExportExcelConsolidated}>XLSX consolidé</button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      {/* Section fichier de déclaration CNSS */}
      {hasPayrollAccess && (
        <div className="statement-generation-section">
          <div className="statement-header">
            <h3>Fichier de Déclaration CNSS</h3>
          </div>
          <div className="statement-controls">
            <div className="form-group">
              <label>Année :</label>
              <input
                type="number"
                value={declarationYear}
                onChange={(e) => setDeclarationYear(parseInt(e.target.value))}
                min="2020"
                max="2100"
              />
            </div>
            <div className="form-group">
              <label>Trimestre :</label>
              <select
                value={declarationQuarter || ''}
                onChange={(e) => setDeclarationQuarter(parseInt(e.target.value))}
              >
                <option value="">--</option>
                <option value="1">T1 (Janvier - Mars)</option>
                <option value="2">T2 (Avril - Juin)</option>
                <option value="3">T3 (Juillet - Septembre)</option>
                <option value="4">T4 (Octobre - Décembre)</option>
              </select>
            </div>
          </div>
          <div className="statement-button-container">
            <button
              className="btn btn-primary"
              onClick={handleGenerateDeclarationFile}
              disabled={generatingDeclarationFile || !declarationQuarter}
            >
              {generatingDeclarationFile ? 'Génération...' : '📄 Générer le fichier de déclaration'}
            </button>
          </div>
        </div>
      )}

      <div className="filters-section">
        <div className="filters-row">
          <div className="filter-group employee-filter">
            <label>Employé</label>
            <div className="employee-select-wrapper">
              <input
                type="text"
                className="employee-search-input"
                placeholder="Rechercher..."
                value={employeeSearch}
                onChange={(e) => {
                  setEmployeeSearch(e.target.value);
                  setShowDropdown(true);
                  if (!e.target.value) setSelectedEmployeeId(null);
                }}
                onFocus={() => setShowDropdown(true)}
                onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
              />
              {selectedEmployeeId && (
                <button className="clear-employee-btn" onClick={() => handleSelectEmployee(null)} title="Effacer">×</button>
              )}
              {showDropdown && filteredEmployees.length > 0 && (
                <div className="employee-dropdown" onMouseDown={(e) => e.preventDefault()}>
                  {filteredEmployees.slice(0, 10).map((emp) => (
                    <div
                      key={emp.id}
                      className={`dropdown-item ${selectedEmployeeId === emp.id ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.preventDefault();
                        handleSelectEmployee(emp);
                      }}
                    >
                      <div className="employee-option">
                        <span className="employee-name">{emp.nom} {emp.prenom}</span>
                        {emp.matricule && <span className="employee-matricule">{emp.matricule}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="filter-group">
            <label>Période</label>
            <select value={mode} onChange={(e) => setMode(e.target.value)}>
              <option value="month">Mois</option>
              <option value="year">Année</option>
              <option value="range">Période (début/fin)</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Année</label>
            <input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} />
          </div>

          {mode === 'month' && (
            <div className="filter-group">
              <label>Mois</label>
              <select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                {monthsOptions.map((m) => (
                  <option key={m} value={m}>
                    {new Date(2000, m - 1).toLocaleString('fr-FR', { month: 'long' })}
                  </option>
                ))}
              </select>
            </div>
          )}

          {mode === 'range' && (
            <>
              <div className="filter-group">
                <label>Début</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    type="number"
                    value={startYear}
                    onChange={(e) => setStartYear(Number(e.target.value))}
                    style={{ width: 90 }}
                  />
                  <select value={startMonth} onChange={(e) => setStartMonth(Number(e.target.value))}>
                    {monthsOptions.map((m) => (
                      <option key={m} value={m}>
                        {new Date(2000, m - 1).toLocaleString('fr-FR', { month: 'short' })}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="filter-group">
                <label>Fin</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    type="number"
                    value={endYear}
                    onChange={(e) => setEndYear(Number(e.target.value))}
                    style={{ width: 90 }}
                  />
                  <select value={endMonth} onChange={(e) => setEndMonth(Number(e.target.value))}>
                    {monthsOptions.map((m) => (
                      <option key={m} value={m}>
                        {new Date(2000, m - 1).toLocaleString('fr-FR', { month: 'short' })}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="table-container">
        {loading ? (
          <div className="loading">Chargement...</div>
        ) : declarations.length === 0 ? (
          <div className="empty-state">Aucune déclaration</div>
        ) : (
          <table className="employee-table">
            <thead>
              <tr>
                <th onClick={() => toggleSort('period')} style={{ cursor: 'pointer' }}>
                  Période {sortBy === 'period' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                </th>
                <th>CNSS total</th>
                <th>IRPP</th>
                <th>CSS total</th>
                <th>Déductions</th>
                <th onClick={() => toggleSort('net')} style={{ cursor: 'pointer' }}>
                  Net {sortBy === 'net' ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                </th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map((d) => (
                <tr key={d.id}>
                  <td>{String(d.periodMonth).padStart(2, '0')}/{d.periodYear}</td>
                  <td>{(d.cnssTotal ?? 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}</td>
                  <td>{(d.irppAmount ?? 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}</td>
                  <td>{(d.cssTotal ?? 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}</td>
                  <td>{(d.totalDeductions ?? 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}</td>
                  <td>{(d.netSalary ?? 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}</td>
                  <td>{d.status}</td>
                  <td style={{ display: 'flex', gap: 6 }}>
                    {hasDeclarationAccess && (
                      <>
                        <button className="btn btn-secondary" onClick={() => handleValidate(d.id)}>Valider</button>
                        <button className="btn btn-secondary" onClick={() => handleSubmit(d.id)}>Soumettre</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {declarations.length > pageSize && (
        <div className="pagination" style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button className="btn btn-secondary" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            Précédent
          </button>
          <span>Page {page} / {totalPages}</span>
          <button className="btn btn-secondary" disabled={page === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
            Suivant
          </button>
        </div>
      )}
    </div>
  );
};

export default DeclarationManagement;

